import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import Modal from '../../../Components/Modal/Modal';
import { fetchToegangsCodesOverzicht, checkToegangsCodeStatus, syncToegangsCodesData, sendCodeToDoorsy, deleteToegangsCodeVolledig } from '../../Admin/Toegangscontrole/Reducers/actions';
import { message } from '../../../Reducers/loading/actions';

const ToegangsCodesOverzicht = ({
  fetchToegangsCodesOverzicht,
  checkToegangsCodeStatus,
  syncToegangsCodesData,
  sendCodeToDoorsy,
  deleteToegangsCodeVolledig,
  message,
  toegangscodes = [],
  loading = false,
  error
}) => {
  const [checkingStatus, setCheckingStatus] = useState({});
  const [statusResult, setStatusResult] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sendingToDoorsy, setSendingToDoorsy] = useState({});
  const [deleting, setDeleting] = useState({});

  useEffect(() => {
    loadToegangsCodesOverzicht();
  }, []);

  const loadToegangsCodesOverzicht = async () => {
    try {
      await fetchToegangsCodesOverzicht();
    } catch (error) {
      console.error('Error loading toegangscodes:', error);
    }
  };

  const handleSyncData = async () => {
    setSyncing(true);
    try {
      await syncToegangsCodesData();
      message(3000, "success", 'Data succesvol gesynchroniseerd!');
      await loadToegangsCodesOverzicht(); // Reload data after sync
    } catch (error) {
      console.error('Error syncing data:', error);
      message(3000, "error", 'Er is een fout opgetreden bij het synchroniseren van data: ' + (error.message || error));
    } finally {
      setSyncing(false);
    }
  };

  const handleSendToDoorsy = async (codeId, codeValue) => {
    setSendingToDoorsy(prev => ({ ...prev, [codeId]: true }));
    try {
      const result = await sendCodeToDoorsy(codeId);
      if (result.success) {
        message(3000, "success", `Code ${codeValue} succesvol verzonden naar RecraPlan!`);
        await loadToegangsCodesOverzicht(); // Reload data after sending
      }
    } catch (error) {
      console.error('Error sending code to Doorsy:', error);
      message(3000, "error", 'Er is een fout opgetreden bij het verzenden naar RecraPlan: ' + (error.message || error));
    } finally {
      setSendingToDoorsy(prev => ({ ...prev, [codeId]: false }));
    }
  };

  const handleCheckStatus = async (uuid, code) => {
    setCheckingStatus(prev => ({ ...prev, [uuid]: true }));

    try {
      const result = await checkToegangsCodeStatus(uuid);

      if (result.success) {
        setStatusResult({
          success: true,
          uuid,
          code,
          data: result.data,
          timestamp: new Date()
        });
        setShowStatusModal(true);
      } else {
        setStatusResult({
          success: false,
          uuid,
          code,
          error: result.message || 'Onbekende fout',
          timestamp: new Date()
        });
        setShowStatusModal(true);
      }
    } catch (error) {
      console.error('Error checking status:', error);
      setStatusResult({
        success: false,
        uuid,
        code,
        error: error.message || 'Onbekende fout',
        timestamp: new Date()
      });
      setShowStatusModal(true);
    } finally {
      setCheckingStatus(prev => ({ ...prev, [uuid]: false }));
    }
  };

  const handleDeleteCode = async (codeId, codeValue) => {
    // Bevestiging vragen
    if (!window.confirm(`Weet je zeker dat je toegangscode ${codeValue} wilt verwijderen? Dit verwijdert de code zowel van Doorsy als uit de database.`)) {
      return;
    }

    setDeleting(prev => ({ ...prev, [codeId]: true }));
    try {
      const result = await deleteToegangsCodeVolledig(codeId);
      if (result.success) {
        message(3000, "success", `Code ${codeValue} succesvol verwijderd!`);
        await loadToegangsCodesOverzicht(); // Reload data after deletion
      }
    } catch (error) {
      console.error('Error deleting code:', error);
      message(3000, "error", 'Er is een fout opgetreden bij het verwijderen: ' + (error.message || error));
    } finally {
      setDeleting(prev => ({ ...prev, [codeId]: false }));
    }
  };

  const getStatusBadge = (toegangscode) => {
    const nu = new Date();
    const vanDatum = new Date(toegangscode.vanDatum);
    const totDatum = new Date(toegangscode.totDatum);

    if (!toegangscode.actief) {
      return <span className="badge bg-secondary">Gedeactiveerd</span>;
    }

    if (nu < vanDatum) {
      return <span className="badge bg-warning">Wacht op start</span>;
    }

    if (nu > totDatum) {
      return <span className="badge bg-danger">Verlopen</span>;
    }

    return <span className="badge bg-success">Actief</span>;
  };

  if (loading) {
    return (
      <div className="card">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </div>
          <p className="mt-2 text-muted">Toegangscodes laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fas fa-key me-2"></i>
          Toegangscodes Overzicht
        </h5>
        <div>
          <button
            className="btn btn-outline-success btn-sm me-2"
            onClick={handleSyncData}
            disabled={syncing || loading}
          >
            {syncing ? (
              <>
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Synchroniseren...
              </>
            ) : (
              <>
                <i className="fas fa-sync me-1"></i>
                Synchroniseer Data
              </>
            )}
          </button>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={loadToegangsCodesOverzicht}
            disabled={loading || syncing}
          >
            <i className="fas fa-sync-alt me-1"></i>
            Vernieuwen
          </button>
        </div>
      </div>

      <div className="card-body">
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        {toegangscodes.length === 0 ? (
          <div className="text-center py-4">
            <i className="fas fa-key fa-3x text-muted mb-3"></i>
            <p className="text-muted">Geen toegangscodes gevonden</p>
            <small className="text-muted">
              Maak eerst een toegangscode aan via het "Toegangscodes" tabblad
            </small>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Van Datum</th>
                  <th>Tot Datum</th>
                  <th>Gast Aankomst</th>
                  <th>Gast Vertrek</th>
                  <th>Status</th>
                  <th>Doorsy UUID</th>
                  <th>Beschrijving</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {toegangscodes.map((code) => (
                  <tr key={code._id}>
                    <td>
                      <span className="font-monospace fw-bold">{code.code}</span>
                    </td>
                    <td>
                      {moment(code.vanDatum).format('DD-MM-YYYY HH:mm')}
                    </td>
                    <td>
                      {moment(code.totDatum).format('DD-MM-YYYY HH:mm')}
                    </td>
                    <td>
                      {code.gastenverbruik_id?.aankomst ? (
                        <span className="text-success">
                          {moment(code.gastenverbruik_id.aankomst).format('DD-MM-YYYY')}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {code.gastenverbruik_id?.vertrek ? (
                        <span className="text-warning">
                          {moment(code.gastenverbruik_id.vertrek).format('DD-MM-YYYY')}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      {getStatusBadge(code)}
                    </td>
                    <td>
                      {code.doorsy?.uuid ? (
                        <span className="font-monospace text-muted small">
                          {code.doorsy.uuid}
                        </span>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <span className="text-muted">
                        {code.beschrijving || 'Geen beschrijving'}
                      </span>
                    </td>
                    <td>
                      <div className="btn-group" role="group">
                        {!code.doorsy?.uuid ? (
                          <button
                            className="btn btn-outline-success btn-sm"
                            onClick={() => handleSendToDoorsy(code._id, code.code)}
                            disabled={sendingToDoorsy[code._id]}
                            title="Verstuur code naar RecraPlan systeem"
                          >
                            {sendingToDoorsy[code._id] ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Verzenden...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-paper-plane me-1"></i>
                                Naar RecraPlan
                              </>
                            )}
                          </button>
                        ) : (
                          <button
                            className="btn btn-outline-info btn-sm"
                            onClick={() => handleCheckStatus(code.doorsy.uuid, code.code)}
                            disabled={checkingStatus[code.doorsy.uuid]}
                            title="Controleer status via Doorsy API"
                          >
                            {checkingStatus[code.doorsy.uuid] ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                Controleren...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-search me-1"></i>
                                Status
                              </>
                            )}
                          </button>
                        )}
                        <button
                          className="btn btn-outline-danger btn-sm ms-1"
                          onClick={() => handleDeleteCode(code._id, code.code)}
                          disabled={deleting[code._id]}
                          title="Verwijder toegangscode volledig (Doorsy + Database)"
                        >
                          {deleting[code._id] ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                              Verwijderen...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-trash me-1"></i>
                              Verwijderen
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {toegangscodes.length > 0 && (
          <div className="mt-3">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Laatste {toegangscodes.length} toegangscodes weergegeven.
              Klik op "Status" om de huidige status via Doorsy API te controleren.
            </small>
          </div>
        )}
      </div>

      {/* Status Result Modal */}
      <Modal
        visible={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        width={800}
        className="status-report-modal"
      >
        {statusResult && (
          <div className="p-4">
            {/* Header */}
            <div className="text-center mb-4">
              <div className="mb-3">
                <i className={`fas ${statusResult.success ? 'fa-shield-check text-success' : 'fa-shield-exclamation text-danger'}`} style={{ fontSize: '3rem' }}></i>
              </div>
              <h3 className="mb-1">Toegangscode Status Rapport</h3>
              <p className="text-muted">Doorsy API Controle Resultaat</p>
            </div>

            {/* Code Info Card */}
            <div className="card mb-4" style={{ backgroundColor: '#f8f9fb', border: '1px solid #e3e6f0' }}>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-4 text-center">
                    <div className="mb-2">
                      <small className="text-muted text-uppercase fw-bold">Toegangscode</small>
                    </div>
                    <div className="display-6 font-monospace text-primary fw-bold">{statusResult.code}</div>
                  </div>
                  <div className="col-md-4 text-center">
                    <div className="mb-2">
                      <small className="text-muted text-uppercase fw-bold">Controle Uitgevoerd</small>
                    </div>
                    <div className="h6 mb-0">{moment(statusResult.timestamp).format('DD-MM-YYYY')}</div>
                    <div className="text-muted">{moment(statusResult.timestamp).format('HH:mm:ss')}</div>
                  </div>
                  <div className="col-md-4 text-center">
                    <div className="mb-2">
                      <small className="text-muted text-uppercase fw-bold">Status</small>
                    </div>
                    <span className={`badge fs-6 px-3 py-2 ${statusResult.success ? 'bg-success' : 'bg-danger'}`}>
                      {statusResult.success ? 'Succesvol' : 'Mislukt'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {statusResult.success ? (
              <div>
                {/* Success Report */}
                <div className="alert alert-success border-0 mb-4" style={{ backgroundColor: '#d1edff', borderLeft: '4px solid #0d6efd' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-check-circle me-3" style={{ fontSize: '1.5rem' }}></i>
                    <div>
                      <h6 className="mb-1 text-success">Verbinding Succesvol</h6>
                      <small className="text-success">De toegangscode status is succesvol opgehaald van de Doorsy API</small>
                    </div>
                  </div>
                </div>

                {/* Status Details */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="fas fa-info-circle me-2"></i>
                          Algemene Informatie
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="mb-3">
                          <strong>Doorsy UUID:</strong>
                          <br />
                          <span className="font-monospace text-muted small">{statusResult.uuid}</span>
                        </div>
                        {statusResult.data.id && (
                          <div className="mb-3">
                            <strong>Internal ID:</strong>
                            <br />
                            <span className="text-muted">{statusResult.data.id}</span>
                          </div>
                        )}
                        {statusResult.data.created && (
                          <div className="mb-3">
                            <strong>Aangemaakt:</strong>
                            <br />
                            <span className="text-muted">{moment(statusResult.data.created).format('DD-MM-YYYY HH:mm')}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card h-100">
                      <div className="card-header bg-light">
                        <h6 className="mb-0">
                          <i className="fas fa-calendar-alt me-2"></i>
                          Geldigheid
                        </h6>
                      </div>
                      <div className="card-body">
                        {statusResult.data.start_date && (
                          <div className="mb-3">
                            <strong>Start Datum:</strong>
                            <br />
                            <span className="text-muted">{moment(statusResult.data.start_date).format('DD-MM-YYYY')}</span>
                          </div>
                        )}
                        {statusResult.data.end_date && (
                          <div className="mb-3">
                            <strong>Eind Datum:</strong>
                            <br />
                            <span className="text-muted">{moment(statusResult.data.end_date).format('DD-MM-YYYY')}</span>
                          </div>
                        )}
                        {statusResult.data.active !== undefined && (
                          <div className="mb-3">
                            <strong>Status in Doorsy:</strong>
                            <br />
                            <span className={`badge ${statusResult.data.active ? 'bg-success' : 'bg-warning'}`}>
                              {statusResult.data.active ? '✓ Actief' : '⚠ Niet Actief'}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Technical Details */}
                {statusResult.data.pincodes && (
                  <div className="card mb-4">
                    <div className="card-header bg-light">
                      <h6 className="mb-0">
                        <i className="fas fa-key me-2"></i>
                        Technische Details
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <strong>PIN Codes:</strong>
                          <br />
                          {statusResult.data.pincodes.map((pin, index) => (
                            <span key={index} className="badge bg-secondary me-2 font-monospace">{pin}</span>
                          ))}
                        </div>
                        {statusResult.data.doorcy_id && (
                          <div className="col-md-6">
                            <strong>Doorcy ID:</strong>
                            <br />
                            <span className="font-monospace text-muted">{statusResult.data.doorcy_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Error Report */}
                <div className="alert alert-danger border-0 mb-4" style={{ backgroundColor: '#f8d7da', borderLeft: '4px solid #dc3545' }}>
                  <div className="d-flex align-items-center">
                    <i className="fas fa-exclamation-triangle me-3" style={{ fontSize: '1.5rem' }}></i>
                    <div>
                      <h6 className="mb-1 text-danger">Controle Mislukt</h6>
                      <small className="text-danger">Er is een fout opgetreden bij het controleren van de toegangscode status</small>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-bug me-2"></i>
                      Fout Details
                    </h6>
                  </div>
                  <div className="card-body">
                    <p className="text-danger mb-0">{statusResult.error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
              <small className="text-muted">
                <i className="fas fa-clock me-1"></i>
                Laatst bijgewerkt: {moment(statusResult.timestamp).format('DD-MM-YYYY HH:mm:ss')}
              </small>
              <div>
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={() => setShowStatusModal(false)}
                >
                  <i className="fas fa-times me-1"></i>
                  Sluiten
                </button>
                {statusResult.success && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const reportText = `Toegangscode Status Rapport

Code: ${statusResult.code}
UUID: ${statusResult.uuid}
Status: ${statusResult.data.active ? 'Actief' : 'Niet Actief'}
Gecontroleerd: ${moment(statusResult.timestamp).format('DD-MM-YYYY HH:mm:ss')}

Technische Details:
${JSON.stringify(statusResult.data, null, 2)}`;
                      navigator.clipboard.writeText(reportText);
                      alert('Status rapport gekopieerd naar klembord!');
                    }}
                  >
                    <i className="fas fa-copy me-1"></i>
                    Kopieer Rapport
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  toegangscodes: state.toegangscontrole?.toegangsCodesOverzicht || [],
  loading: state.toegangscontrole?.loading || false,
  error: state.toegangscontrole?.error
});

export default connect(mapStateToProps, {
  fetchToegangsCodesOverzicht,
  checkToegangsCodeStatus,
  syncToegangsCodesData,
  sendCodeToDoorsy,
  deleteToegangsCodeVolledig,
  message
})(ToegangsCodesOverzicht);